import React, { useState } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Button from "../button";
import TextInput from "../inputs/TextInput";
import Loading from "../loading";
import StudentResultList from "./StudentResultList";
import { useSchoolFeeStudentSearch } from "../../api/queries";
import { useSchoolFee } from "../../context/schoolFeeContext";
import { getFailureDisplay, getStorageToken } from "../../utils";
import { SchoolFeeStudentResult } from "../../models/schoolFee";

// The server answers a `name` shorter than this with an empty HTTP 200 rather
// than a validation error, so without the same minimum on the client a payer
// cannot tell "too short to search" from "no such student". It is stated on
// screen for the same reason.
export const MIN_NAME_LENGTH = 3;

type Criteria = { matricule?: string; name?: string };

/**
 * Journey A's identity step: find the student on the school's roster.
 *
 * It owns the search rather than the template because the RESULT is what
 * advances the journey -- there is no Continue button here to press early.
 */
const StudentSearchForm = ({ coreServiceId }: { coreServiceId: string }) => {
  const { t, i18n } = useTranslation();
  const { selectStudent } = useSchoolFee();
  const token = getStorageToken();

  const [matricule, setMatricule] = useState("");
  const [name, setName] = useState("");
  // The criteria a search was actually RUN with, held apart from what is in
  // the fields: typing after a search must not silently re-key the query and
  // replace the list the payer is reading.
  const [criteria, setCriteria] = useState<Criteria | null>(null);

  const nameTooShort =
    name.trim() !== "" && name.trim().length < MIN_NAME_LENGTH;
  const canSearch =
    matricule.trim() !== "" || name.trim().length >= MIN_NAME_LENGTH;

  const search = useSchoolFeeStudentSearch({
    coreServiceId,
    matricule: criteria?.matricule,
    name: criteria?.name,
    authorisation: token || "",
    enabled: !!criteria,
  });

  const onSubmit = () => {
    if (!canSearch) return;

    // Matricule is the precise identifier, so it wins when both are filled in;
    // sending both would have the server disambiguate a choice the payer
    // already made by typing the number.
    setCriteria(
      matricule.trim() !== ""
        ? { matricule: matricule.trim() }
        : { name: name.trim() }
    );
  };

  // Selecting is the whole of this step's output: the context writes the
  // identity and moves the journey on in one update.
  const onSelect = (hit: SchoolFeeStudentResult) => selectStudent(hit);

  const results = search.data?.items ?? [];
  const searched = !!criteria && search.isSuccess;

  return (
    <Box mb={4} data-testid="school-fee-student-search">
      <Typography variant="h6" fontWeight="bold" mb={2}>
        {t("schoolfee.steps.identity")}
      </Typography>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 1, md: 2 }}
        alignItems={{ xs: "stretch", md: "flex-start" }}
      >
        <Box flex={1}>
          <TextInput
            fullWidth
            type="text"
            id="schoolfee-matricule"
            label={t("schoolfee.search.matricule_label")}
            value={matricule}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setMatricule(event.target.value)
            }
            placeholder={t("schoolfee.search.matricule_placeholder")}
            variant="outlined"
          />
        </Box>

        {/* The two boxes are ALTERNATIVES, not a pair to fill in, and nothing
            on screen said so -- a payer could reasonably read them as both
            required. It is centred on the field row (56px is MUI's outlined
            height) so it does not drift down beside the name field's helper
            text, and it takes its colour from the theme rather than a literal,
            because branding is resolved per corporate group here. */}
        <Typography
          data-testid="school-fee-search-or"
          color="primary.main"
          fontWeight={700}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: { md: 56 },
          }}
        >
          {t("schoolfee.search.or")}
        </Typography>

        <Box flex={1}>
          <TextInput
            fullWidth
            type="text"
            id="schoolfee-name"
            label={t("schoolfee.search.name_label")}
            value={name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setName(event.target.value)
            }
            error={nameTooShort}
            helperText={t("schoolfee.search.name_minimum")}
            placeholder={t("schoolfee.search.name_placeholder")}
            variant="outlined"
          />
        </Box>
      </Stack>

      <Box mt={3}>
        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={!canSearch || search.isFetching}
          isLoading={search.isFetching}
          onClick={onSubmit}
        >
          {t("schoolfee.search.submit")}
        </Button>
      </Box>

      {search.isFetching && <Loading />}

      {search.isError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {getFailureDisplay(search.error, t, i18n.language).message}
        </Alert>
      )}

      {/* D-24 / D-25: nothing found is an EMPTY RESULT rendered inline. It is
          not an error surface, it does not take the screen, and it offers no
          doorway to the catalogue -- the searchable journey is the only one
          this service has. */}
      {searched && results.length === 0 && (
        <Box mt={3} data-testid="school-fee-search-empty">
          <Typography variant="body1" fontWeight="700">
            {t("schoolfee.search.empty_title")}
          </Typography>
          <Typography sx={{ fontSize: 13 }}>
            {t("schoolfee.search.empty_body")}
          </Typography>
        </Box>
      )}

      {searched && results.length > 0 && (
        <StudentResultList results={results} onSelect={onSelect} />
      )}
    </Box>
  );
};

export default StudentSearchForm;
